import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { toast } from "sonner";
import { ArrowLeft, Archive, RotateCcw, ShieldAlert, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { makeApi } from "@/lib/api";
import { licenseTier, TIER_STYLES } from "@/lib/license";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { CredentialsDialog } from "@/components/drivers/CredentialsDialog";
import { ProfileFields, PROFILE_KEYS, profileValid, profileBody } from "@/components/drivers/ProfileFields";
import { UsernameField, USERNAME_OK } from "@/components/drivers/AddDriverDialog";
import { LoginCard } from "@/components/drivers/LoginCard";
import { DriverDocs } from "@/components/drivers/DriverDocs";
import { errMsg } from "@/pages/Drivers";

const toForm = (d) => ({ ...Object.fromEntries(PROFILE_KEYS.map((k) => [k, d[k] || ""])), username: d.username || "" });

const Section = ({ title, desc, children, testId }) => (
  <Card className="p-5 md:p-6 border-slate-200" data-testid={testId}>
    <h2 className="text-lg font-semibold" style={{ fontFamily: "Outfit" }}>{title}</h2>
    {desc && <p className="text-sm text-slate-500 mt-0.5">{desc}</p>}
    <div className="mt-4">{children}</div>
  </Card>
);

const LicenseWarning = ({ expiry }) => {
  const t = licenseTier(expiry);
  if (!t) return null;
  return (
    <div className={`flex items-center gap-2.5 rounded-xl border px-4 py-3 text-sm font-medium ${TIER_STYLES[t.tier]}`} data-testid="driver-license-warning" data-tier={t.tier}>
      <ShieldAlert className="w-5 h-5 shrink-0" /> {t.label} ({expiry}). {t.tier === "urgent" ? "This driver shouldn't run loads until it's renewed." : "Remind them to renew it."}
    </div>
  );
};

export default function DriverProfile() {
  const { id } = useParams();
  const { getToken } = useAuth();
  const api = useMemo(() => makeApi(getToken), [getToken]);
  const [d, setD] = useState(null);
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [creds, setCreds] = useState(null);
  const [archiving, setArchiving] = useState(false);
  const [userStatus, setUserStatus] = useState("ok");

  const refresh = async (resetForm = false) => {
    try {
      const { data } = await api.get(`/admin/drivers/${id}`);
      setD(data);
      if (resetForm) setF(toForm(data));
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { refresh(true); /* eslint-disable-next-line */ }, [id]);

  if (!d || !f) return <div className="text-slate-500">Loading…</div>;
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const dirty = JSON.stringify(f) !== JSON.stringify(toForm(d));
  const valid = profileValid(f) && USERNAME_OK.test(f.username) && userStatus === "ok";
  const archived = d.status === "archived";

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/drivers/${id}`, { ...profileBody(f), username: f.username });
      setD(data);
      setF(toForm(data));
      toast.success("Driver profile saved");
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  const archive = async () => {
    try {
      const { data } = await api.post(`/admin/drivers/${id}/archive`);
      toast.success(`${d.name} archived${data.unassigned_loads ? ` · ${data.unassigned_loads} load(s) unassigned` : ""}`);
      setArchiving(false);
      refresh();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const restore = async () => {
    try { await api.post(`/admin/drivers/${id}/restore`); toast.success(`${d.name} restored`); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="max-w-4xl space-y-6" data-testid="driver-profile-page">
      <Link to="/admin/drivers" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900" data-testid="driver-profile-back"><ArrowLeft className="w-4 h-4" /> All drivers</Link>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }} data-testid="driver-profile-name">{d.name}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-600">
            <span className={`pill ${archived ? "bg-slate-100 text-slate-600 border border-slate-200" : "bg-emerald-50 text-emerald-700 border border-emerald-200"}`} data-testid="driver-profile-status">{archived ? "Archived" : "Active"}</span>
            <span data-testid="driver-profile-active-loads">{d.active_loads} active load{d.active_loads === 1 ? "" : "s"}</span>
          </div>
        </div>
        <div className="flex gap-2">
          {archived ? (
            <Button variant="outline" onClick={restore} data-testid="driver-restore-btn"><RotateCcw className="w-4 h-4 mr-1.5" /> Restore</Button>
          ) : (
            <Button variant="outline" onClick={() => setArchiving(true)} className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" data-testid="driver-archive-btn"><Archive className="w-4 h-4 mr-1.5" /> Archive</Button>
          )}
          <Button onClick={save} disabled={!dirty || !valid || saving} className="bg-[#002FA7] hover:bg-[#001A75] rounded-full" data-testid="driver-profile-save-btn">
            {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}{dirty ? "Save changes" : "Saved"}
          </Button>
        </div>
      </div>

      {!archived && <LicenseWarning expiry={d.license_expiry} />}

      <Section title="Login" desc="The driver signs in at the driver login page with these." testId="driver-profile-login">
        <div className="space-y-4">
          <div className="max-w-sm"><UsernameField value={f.username} onChange={(v) => set("username", v)} excludeId={d.user_id} original={d.username || ""} onStatus={setUserStatus} /></div>
          <LoginCard driver={d} api={api} onReset={(pw) => { setCreds({ title: "Password reset", username: d.username, password: pw }); refresh(); }} />
        </div>
      </Section>
      <Section title="Profile" testId="driver-profile-details"><ProfileFields f={f} set={set} /></Section>
      <Section title="Documents" desc="License photo, medical card, employment paperwork and anything else for this driver." testId="driver-profile-docs">
        <DriverDocs driver={d} api={api} getToken={getToken} onChanged={() => refresh()} />
      </Section>

      <CredentialsDialog creds={creds} onClose={() => setCreds(null)} />
      <ConfirmDialog open={archiving} onCancel={() => setArchiving(false)} title={`Archive ${d.name}?`} confirmLabel="Archive" onConfirm={archive} testId="archive-driver-dialog"
        description={`They can no longer sign in, and they're taken off ${d.active_loads || 0} unfinished load(s). Delivered and invoiced loads keep their name. You can restore them later.`} />
    </div>
  );
}
